/* eslint-disable max-lines */
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GeometryType, Permissions, ResourceTypeLink, ServiceInfoDc, TableInfoDc } from "@evergis/api";
import { Column, Icon, IconTypesKeys, Row, SortType as UISortType } from "@evergis/ui";

import { NoContent } from "../CardList/NoContent";
import { NoTables } from "../CardList/NoTables";
import { CatalogTable } from "./styled";
import { TableControls } from "./TableControls";
import { TableName } from "./TableName";

import { formatDate } from "../../../../utils/date";
import { getCategory } from "../../../../ducks/catalog/selectors";
import { useResize } from "../../../../hooks/useResize";
import { useTableActionsHandler } from "./useTableActionsHandler";
import { useResourceEmpty } from "../../hooks/useResourceEmpty";
import { useSelectedResources } from "../../hooks/useSelectedResources";
import { useSort } from "../../hooks/useSort";
import { useResourceType } from "../../../../hooks/useResourceType";
import { isConfigure } from "../../../../utils/permissions";
import { isFilterEmpty, ResourceCategory, useFetchParams } from "../../hooks/useFetchParams";

import { EditGeometryType, geometryTypes } from "../../../../evergis/core/attributes/geometryTypes";
import { TableMenuValues } from "../../../Table/types";
import { ResourcesListProps } from "../../types";
import { SortType } from "../Sort/types";

const MIN_TABLE_HEIGHT = 400;
const FOOTER_HEIGHT = 40;

// eslint-disable-next-line max-lines-per-function
export const TableListComponent: FC<ResourcesListProps> = ({
  resources = [],
  currentPage,
  onPageChange,
  totalCount,
  loading,
  pageSize: currentPageSize,
}) => {
  const { t } = useTranslation("catalog");
  const container = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);
  const [sort, setSort] = useSort();
  const [resourceType] = useResourceType();

  const tableColumns: Column[] = useMemo(
    () => [
      { id: SortType.Alias, alias: t("tableName"), accessor: SortType.Alias, Header: t("tableName"), width: 250 },
      {
        id: SortType.Description,
        alias: t("description"),
        accessor: SortType.Description,
        Header: t("description"),
        noSort: true,
      },
      {
        id: SortType.Permissions,
        accessor: SortType.Permissions,
        Header: t("permissions"),
        alias: t("permissions"),
        noSort: true,
        tdClassName: "noActionCell",
        width: 120,
      },
      {
        id: SortType.Type,
        accessor: SortType.Type,
        Header: t("type"),
        alias: t("type"),
        tdClassName: "noActionCell",
        width: 100,
      },
      {
        id: SortType.CreatedDate,
        accessor: SortType.CreatedDate,
        Header: t("created"),
        alias: t("created"),
        type: "datetime",
      },
      {
        id: SortType.ChangedDate,
        accessor: SortType.ChangedDate,
        Header: t("changed"),
        alias: t("changed"),
        type: "datetime",
      },
      { id: SortType.Owner, accessor: SortType.Owner, Header: t("owner"), alias: t("owner") },
    ],
    [t],
  );

  const excludedColumns = useMemo((): string[] => {
    const excluded = ["controls"];

    if (resourceType === ResourceTypeLink.Project) {
      excluded.concat([SortType.Type, SortType.Count]);
    }

    if (resourceType !== ResourceTypeLink.Layer) {
      excluded.push(SortType.Permissions);
    }

    return excluded;
  }, [resourceType]);

  const [excludedData, setExcludedData] = useState([]);
  const fetchParams = useFetchParams();
  const [selected, , toggleSelected] = useSelectedResources();
  const userHasNoData = !loading && resources?.length === 0;
  const userHasNoResource = userHasNoData && isFilterEmpty(fetchParams);
  const category = useSelector(getCategory(resourceType));

  useResourceEmpty(userHasNoResource);

  const getVisibleColumns = useMemo(() => {
    return tableColumns.filter(col => !excludedColumns.includes(col.id as string));
  }, [tableColumns, excludedColumns]);

  const getData = useMemo(() => {
    const visibleNames = getVisibleColumns.map(col => col.id);

    return resources.map(res => {
      const data: Row["data"] = {
        controls: <TableControls resource={res} config={{ infoIcon: { title: "" } }} />,
        resourceAlias: res.alias,
      };

      if (visibleNames.includes(SortType.Alias)) {
        data[SortType.Alias] = <TableName resource={res} />;
      }
      if (visibleNames.includes(SortType.Description)) {
        data[SortType.Description] = res.description;
      }
      if (visibleNames.includes(SortType.Permissions) && resourceType === ResourceTypeLink.Layer) {
        data[SortType.Permissions] = (
          <>
            {(res as ServiceInfoDc).minResolution || (res as ServiceInfoDc).maxResolution ? (
              <Icon style={{ marginRight: "1rem" }} kind={"minimize"} />
            ) : null}
            {(res as ServiceInfoDc).condition ? <Icon kind={"filter"} /> : null}
          </>
        );
      }
      if (visibleNames.includes(SortType.Type) && resourceType === ResourceTypeLink.Layer) {
        const geometryType = geometryTypes.find(({ value }) =>
          (res as ServiceInfoDc).type === "PostgresTileLayerService"
            ? value === EditGeometryType.Raster
            : value === ((res as ServiceInfoDc).geometryType as GeometryType),
        );

        data[SortType.Type] = (
          <Icon
            title={
              geometryType
                ? t(geometryType.langKey, { ns: "common" })
                : (res as ServiceInfoDc).geometryType === GeometryType.Unknown
                ? t("withoutGeometry", { ns: "common" })
                : ""
            }
            kind={
              (res as ServiceInfoDc).type === "PostgresTileLayerService"
                ? "geom_raster"
                : (res as ServiceInfoDc).type === "PostgresLayerService"
                ? (`geom_${(res as ServiceInfoDc).geometryType}` as IconTypesKeys)
                : null
            }
          />
        );
      }
      if (visibleNames.includes(SortType.Type) && resourceType === ResourceTypeLink.Table) {
        const type = (res as TableInfoDc).geometries[0];

        data[SortType.Type] = <Icon kind={`geom_${type}` as IconTypesKeys} />;
      }
      if (visibleNames.includes(SortType.Count) && resourceType === ResourceTypeLink.Layer) {
        data[SortType.Count] = (res as ServiceInfoDc).objectCount;
      }
      if (visibleNames.includes(SortType.Count) && resourceType === ResourceTypeLink.Table) {
        data[SortType.Count] = (res as TableInfoDc).rowCount;
      }
      if (visibleNames.includes(SortType.CreatedDate)) {
        data[SortType.CreatedDate] = formatDate(res.createdDate);
      }
      if (visibleNames.includes(SortType.ChangedDate)) {
        data[SortType.ChangedDate] = formatDate(res.changedDate);
      }
      if (visibleNames.includes(SortType.Owner)) {
        data[SortType.Owner] = res.owner;
      }

      const menuOptions = [];

      if (
        category === ResourceCategory.User ||
        (category === ResourceCategory.Shared && res.permissions === Permissions.All)
      ) {
        menuOptions.push({ value: TableMenuValues.Settings, icon: "settings", text: t("settings", { ns: "common" }) });
      }
      if (
        category === ResourceCategory.User ||
        (category === ResourceCategory.Shared && isConfigure(res.permissions))
      ) {
        menuOptions.push({ value: TableMenuValues.Access, icon: "key", text: t("accessSettings", { ns: "common" }) });
      }

      menuOptions.push({ value: TableMenuValues.Copy, icon: "copy", text: t("actions.copy", { ns: "common" }) });

      menuOptions.push({
        value: TableMenuValues.Export,
        icon: "download",
        text: t("actions.export", { ns: "common" }),
      });

      if (
        category === ResourceCategory.User ||
        (category === ResourceCategory.Shared && isConfigure(res.permissions))
      ) {
        menuOptions.push({
          value: TableMenuValues.Delete,
          icon: "delete",
          text: t("actions.delete", { ns: "common" }),
        });
      }

      return {
        id: res.name,
        data,
        menuOptions,
      };
    });
  }, [getVisibleColumns, resourceType, category, resources, t]);

  const onToggleSort = useCallback(
    (id: string, type: string) => {
      setSort({ ...sort, [id]: type as UISortType });
    },
    [sort, setSort],
  );

  const onSelect = useCallback(
    (select: string[]) => {
      toggleSelected(resources.filter(res => select.includes(res.name)));
    },
    [resources, toggleSelected],
  );

  const getTableColumns = useCallback((): Column[] => {
    let newTableColumns = getVisibleColumns;

    if (resourceType === ResourceTypeLink.Project) {
      newTableColumns = newTableColumns.filter(col => ![SortType.Type, SortType.Count].includes(col.id as SortType));
    }

    if (resourceType !== ResourceTypeLink.Layer) {
      newTableColumns = newTableColumns.filter(col => (col.id as SortType) !== SortType.Permissions);
    }

    if (resourceType !== ResourceTypeLink.Layer) {
      newTableColumns.push({
        id: "controls",
        accessor: "controls",
        Header: null,
        noSort: true,
        width: 180,
      });
    }

    return newTableColumns;
  }, [getVisibleColumns, resourceType]);

  const getSelectedData = useCallback(() => selected.map(res => res.name), [selected]);

  const onTableHeight = useCallback(() => {
    const height = container.current?.offsetHeight ? container.current?.offsetHeight - FOOTER_HEIGHT : MIN_TABLE_HEIGHT;

    setTableHeight(height);
  }, []);

  const onMenuSelect = useTableActionsHandler();

  useEffect(() => {
    onTableHeight();
  }, []); // eslint-disable-line

  useResize(onTableHeight);

  if (userHasNoResource) {
    return <NoTables headerText={t("createTableMessage")} />;
  }

  if (userHasNoData) {
    return <NoContent />;
  }

  return (
    <div style={{ height: "100%" }} ref={container}>
      <CatalogTable
        selectedData={getSelectedData()}
        totalRowsCount={totalCount}
        columns={getTableColumns()}
        width={"100%"}
        height={tableHeight}
        data={getData}
        sort={sort}
        onSelect={onSelect}
        onToggleSort={onToggleSort}
        special={{
          width: 70,
        }}
        totalText={t("total")}
        pageSize={currentPageSize}
        onPageChange={onPageChange}
        currentPage={currentPage}
        onMenuSelect={onMenuSelect}
        excludedData={excludedData}
        onExcludeData={setExcludedData}
        excludedSettingsColumns={excludedColumns}
      />
    </div>
  );
};

export const TableList: FC<ResourcesListProps> = memo(TableListComponent);
